import React, { useEffect, useState, useTransition, useDeferredValue } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import WorkflowStatusBar from "./WorkflowStatusBar";
import { runSharedKpiCalculations } from "../utils/kpiCalculationSync";


let API = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const KPI_API_URL =
  `${API}/api/kpi`;
const KPI_SEARCH_API_URL = `${API}/api/kpi/search`;
const KPI_SELECTIONS_KEY = "kpiSelectorsSelections";
const NEWLY_ADDED_KPI_IDS_KEY = "newlyAddedKpiIds";
const KPI_KEY_SEPARATOR = "::kpi::";

type SelectedKpiRow = {
  category: string;
  kpi: string;
};

const extractKpiRows = (payload) => {
  if (Array.isArray(payload?.kpis)) {
    return payload.kpis;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};


function buildApiCandidates(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const candidates = [
    API + normalizedPath,
  ].filter(Boolean);

  return [...new Set(candidates)];
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal, credentials: 'include' });
  } finally {
    clearTimeout(timer);
  }
}

async function requestWithFallback(path, options = {}, timeoutMs = 20000) {
  const urls = buildApiCandidates(path);
  let lastError = null;

  for (const url of urls) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isHtmlResponse = contentType.includes('text/html');

      if (response.ok && !isHtmlResponse) return response;

      if (response.ok && isHtmlResponse) {
        lastError = new Error(`Received HTML instead of API response from ${url}`);
        continue;
      }

      // Try to extract error details from response body
      let errorDetail = `Request failed (${response.status})`;
      try {
        const errorBody = await response.text();
        if (errorBody) {
          const parsed = JSON.parse(errorBody);
          errorDetail = parsed.message || parsed.error || errorBody || errorDetail;
        }
      } catch {
        // If we can't parse, just use status code
      }

      lastError = new Error(errorDetail);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Request failed');
}

const makeKpiKey = (category, kpiName) => `${category}${KPI_KEY_SEPARATOR}${kpiName}`;

const parseKpiKey = (kpiKey): SelectedKpiRow | null => {
  const separatorIndex = kpiKey.indexOf(KPI_KEY_SEPARATOR);

  if (separatorIndex === -1) {
    return null;
  }

  return {
    category: kpiKey.slice(0, separatorIndex),
    kpi: kpiKey.slice(separatorIndex + KPI_KEY_SEPARATOR.length),
  };
};

const getInitialKpiSelections = () => {
  try {
    const raw = localStorage.getItem(KPI_SELECTIONS_KEY);
    if (!raw) {
      return {
        selectedKpiCategory: "",
        selectedKpisByCategory: {},
        selectedKpiOrder: [],
      };
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {
        selectedKpiCategory: "",
        selectedKpisByCategory: {},
        selectedKpiOrder: [],
      };
    }

    const selectedKpisByCategory =
      parsed.selectedKpisByCategory &&
      typeof parsed.selectedKpisByCategory === "object" &&
      !Array.isArray(parsed.selectedKpisByCategory)
        ? Object.fromEntries(
            Object.entries(parsed.selectedKpisByCategory).map(([key, value]) => [
              key,
              Array.isArray(value)
                ? value.filter((item) => typeof item === "string")
                : [],
            ]),
          )
        : {};

    const fallbackOrder = Object.entries(selectedKpisByCategory).flatMap(
      ([category, kpis]) => kpis.map((kpi) => makeKpiKey(category, kpi)),
    );

    return {
      selectedKpiCategory:
        typeof parsed.selectedKpiCategory === "string"
          ? parsed.selectedKpiCategory
          : "",
      selectedKpisByCategory,
      selectedKpiOrder: Array.isArray(parsed.selectedKpiOrder)
        ? parsed.selectedKpiOrder.filter((item) => typeof item === "string")
        : fallbackOrder,
    };
  } catch {
    return {
      selectedKpiCategory: "",
      selectedKpisByCategory: {},
      selectedKpiOrder: [],
    };
  }
};

const KpiSelectors = ({
  compareCompanyView,
  onContinue,
  onSelectionChange,
  isEnabled = true,
  hideContinueButton = false,
}) => {
  const initialSelections = getInitialKpiSelections();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [selectedKpiCategory, setSelectedKpiCategory] = useState(
    initialSelections.selectedKpiCategory,
  );
  const [selectedKpisByCategory, setSelectedKpisByCategory] = useState(
    initialSelections.selectedKpisByCategory,
  );
  const [selectedKpiOrder, setSelectedKpiOrder] = useState(
    initialSelections.selectedKpiOrder,
  );
  const [draggedKpiKey, setDraggedKpiKey] = useState("");
  const [draggedCategoryKey, setDraggedCategoryKey] = useState("");
  // { key: string, after: boolean } — which KPI row to show indicator on
  const [kpiDropIndicator, setKpiDropIndicator] = useState(null);
  // { category: string, after: boolean } — which category header to show indicator on
  const [catDropIndicator, setCatDropIndicator] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kpiSearchQuery, setKpiSearchQuery] = useState("");
  const deferredKpiSearchQuery = useDeferredValue(kpiSearchQuery);
  const [isPending, startTransition] = useTransition();
  const [kpiSearchMessage, setKpiSearchMessage] = useState("");
  const [uploadedTempFileName, setUploadedTempFileName] = useState("");
  const [showAddKpiModal, setShowAddKpiModal] = useState(false);
  const [newKpiName, setNewKpiName] = useState('');
  const [newKpiDescription, setNewKpiDescription] = useState('');
  const [newKpiSource, setNewKpiSource] = useState('');
  const [newKpiCategory, setNewKpiCategory] = useState('');
  const [newKpiFunction, setNewKpiFunction] = useState('');
  const [addKpiApiMessage, setAddKpiApiMessage] = useState('');
  const [showErrorOnAddKpi, setShowErrorOnAddKpi] = useState(false);
  const [addKpiLoading, setAddKpiLoading] = useState(false);
  const [kpiFunctions, setKpiFunctions] = useState([]);
  const [kpiSources, setKpiSources] = useState([]);
  const [savedKpiItems, setSavedKpiItems] = useState([]);
  const [savedKpiIdSet, setSavedKpiIdSet] = useState(new Set());
  const [savedKpiLoading, setSavedKpiLoading] = useState(false);
  const [saveSelectedKpisLoading, setSaveSelectedKpisLoading] = useState(false);
  const [saveSelectedKpisMessage, setSaveSelectedKpisMessage] = useState("");
  const [showSaveSelectedKpisError, setShowSaveSelectedKpisError] = useState(false);
  const [showEditSavedKpisModal, setShowEditSavedKpisModal] = useState(false);
  const [deletingSavedKpiIds, setDeletingSavedKpiIds] = useState({});
  const [editSavedKpisMessage, setEditSavedKpisMessage] = useState("");
  const [showEditSavedKpisError, setShowEditSavedKpisError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    description: '',
    category: '',
    function: '',
  });

  useEffect(() => {
    if (!showAddKpiModal && !showEditSavedKpisModal) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (showAddKpiModal) {
          setShowAddKpiModal(false);
        }
        if (showEditSavedKpisModal) {
          setShowEditSavedKpisModal(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showAddKpiModal, showEditSavedKpisModal]);

  useEffect(() => {
    setUploadedTempFileName(localStorage.getItem("uploadedTempFileName") || "");
  }, []);

  // Fetch KPI functions for Add KPI modal dropdown
  useEffect(() => {
    let isMounted = true;
    fetch(KPI_API_URL, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        const kpis = data.kpis || [];
        const functions = [...new Set(kpis.map((k) => k.Function).filter(Boolean))];
        setKpiFunctions(functions);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Fetch KPI sources for Add KPI modal dropdown
  useEffect(() => {
    let isMounted = true;
    fetch(`${API}/api/kpi/sources`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        setKpiSources(data.sources || []);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        KPI_SELECTIONS_KEY,
        JSON.stringify({
          selectedKpiCategory,
          selectedKpisByCategory,
          selectedKpiOrder,
        }),
      );
    } catch {
      // ignore storage errors
    }
  }, [selectedKpiCategory, selectedKpisByCategory, selectedKpiOrder]);

  useEffect(() => {
    let isMounted = true;

    const fetchKpis = async () => {
      setLoading(true);
      setKpiSearchMessage("");

      try {
        const response = await axios.get(KPI_API_URL, { withCredentials: true });
        const nextData = extractKpiRows(response.data);

        if (isMounted) {
          setData(nextData);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching data:", error);
          setData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchKpis();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadSavedKpis = async () => {
    setSavedKpiLoading(true);

    try {
      const response = await requestWithFallback(
        "/api/kpis/saved",
        {
          method: "GET",
        },
      );
      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setSavedKpiItems(items);
      const nextSavedIds = new Set(
        items
          .map((item) => Number(item?.kpi_id))
          .filter((id) => Number.isInteger(id) && id > 0),
      );
      setSavedKpiIdSet(nextSavedIds);
    } catch (error) {
      console.error("Error fetching saved KPI list:", error);
      setSavedKpiItems([]);
      setSavedKpiIdSet(new Set());
    } finally {
      setSavedKpiLoading(false);
    }
  };

  useEffect(() => {
    loadSavedKpis();
  }, []);

  const kpiCategories = [...new Set(data.map((kpi) => kpi.KPI_Category).filter(Boolean))];
  const selectedKpiKeys = Object.entries(selectedKpisByCategory).flatMap(
    ([category, kpis]) => kpis.map((kpi) => makeKpiKey(category, kpi)),
  );

  useEffect(() => {
    const selectedKeysFromCategory = Object.entries(selectedKpisByCategory).flatMap(
      ([category, kpis]) => kpis.map((kpi) => makeKpiKey(category, kpi)),
    );
    const selectedKeySet = new Set(selectedKeysFromCategory);

    setSelectedKpiOrder((previousOrder) => {
      const retained = previousOrder.filter((kpiKey) => selectedKeySet.has(kpiKey));
      const missing = selectedKeysFromCategory.filter(
        (kpiKey) => !retained.includes(kpiKey),
      );
      const nextOrder = [...retained, ...missing];
      const isSameOrder =
        previousOrder.length === nextOrder.length &&
        previousOrder.every((kpiKey, index) => kpiKey === nextOrder[index]);

      return isSameOrder ? previousOrder : nextOrder;
    });
  }, [selectedKpisByCategory]);

  const selectedKeySet = new Set(selectedKpiKeys);
  const tableRows: SelectedKpiRow[] = selectedKpiOrder
    .map(parseKpiKey)
    .filter(
      (row): row is SelectedKpiRow =>
        row &&
        row.category &&
        row.kpi &&
        selectedKeySet.has(makeKpiKey(row.category, row.kpi)),
    );

  const selectedKpisGroupedByCategory = tableRows.reduce<Record<string, string[]>>((accumulator, row) => {
    if (!accumulator[row.category]) {
      accumulator[row.category] = [];
    }

    accumulator[row.category].push(row.kpi);
    return accumulator;
  }, {});

  const completedKpiSelectionsCount = tableRows.length > 0 ? 2 : 1;
  const stripWidthPercent = Math.max(
    18,
    (completedKpiSelectionsCount / 2) * 100,
  );
  const isKpiInteractionEnabled = Boolean(isEnabled);
  const selectedKpiIds = [...new Set(
    tableRows
      .flatMap((selectedRow) =>
        data.filter(
          (kpi) =>
            kpi.KPI_Category === selectedRow.category &&
            kpi.KPI_Name === selectedRow.kpi,
        ),
      )
      .map((kpi) => Number(kpi.id ?? kpi.kpi_id))
      .filter((id) => Number.isInteger(id) && id > 0),
  )];
  const unsavedSelectedKpiIds = selectedKpiIds.filter(
    (id) => !savedKpiIdSet.has(id),
  );
  const canSaveSelectedKpis =
    unsavedSelectedKpiIds.length > 0 &&
    !saveSelectedKpisLoading &&
    !savedKpiLoading &&
    isKpiInteractionEnabled;
  const savedKpisInCatalog = data.filter((kpi) => {
    const kpiId = Number(kpi.id ?? kpi.kpi_id);
    return Number.isInteger(kpiId) && kpiId > 0 && savedKpiIdSet.has(kpiId);
  });
  const allSavedKpiKeysInCatalog = [...new Set(
    savedKpisInCatalog
      .map((kpi) => makeKpiKey(kpi.KPI_Category, kpi.KPI_Name))
      .filter((key) => {
        const parsed = parseKpiKey(key);
        return parsed && parsed.category && parsed.kpi;
      }),
  )];
  const areAllSavedKpisSelected =
    allSavedKpiKeysInCatalog.length > 0 &&
    allSavedKpiKeysInCatalog.every((kpiKey) => selectedKeySet.has(kpiKey));

  useEffect(() => {
    try {
      localStorage.setItem("selectedKpiRows", JSON.stringify(tableRows));
      localStorage.setItem("selectedKpiIds", JSON.stringify(selectedKpiIds));
    } catch {
      // ignore storage errors
    }

    if (typeof onSelectionChange === "function") {
      onSelectionChange({
        hasKpiSelection: tableRows.length > 0,
        selectionSignature: JSON.stringify({ tableRows, selectedKpiIds }),
      });
    }
  }, [tableRows, selectedKpiIds, onSelectionChange]);

  const handleSaveSelectedKpis = async () => {
    if (!canSaveSelectedKpis) {
      return;
    }

    setSaveSelectedKpisLoading(true);
    setSaveSelectedKpisMessage("");
    setShowSaveSelectedKpisError(false);

    try {
      await requestWithFallback(
        "/api/kpis/saved",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            kpi_ids: unsavedSelectedKpiIds,
          }),
        },
        120000,
      );

      await loadSavedKpis();
      setSaveSelectedKpisMessage("Selected KPIs saved successfully.");
      setShowSaveSelectedKpisError(false);
    } catch (error) {
      console.error("Error saving selected KPIs:", error);
      setSaveSelectedKpisMessage(
        error?.message || "Unable to save selected KPIs. Please try again.",
      );
      setShowSaveSelectedKpisError(true);
    } finally {
      setSaveSelectedKpisLoading(false);
    }
  };

  const handleSavedKpisCheckboxChange = (event) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    const shouldSelectSaved = Boolean(event.target.checked);

    startTransition(() => {
      if (shouldSelectSaved) {
        if (savedKpisInCatalog.length > 0) {
          setSelectedKpiCategory(savedKpisInCatalog[0].KPI_Category || "");
        }

        setSelectedKpisByCategory((previous) => {
          const nextState = { ...previous };

          savedKpisInCatalog.forEach((kpi) => {
            const category = kpi.KPI_Category;
            const name = kpi.KPI_Name;
            if (!category || !name) {
              return;
            }

            const currentSelection = nextState[category] || [];
            if (!currentSelection.includes(name)) {
              nextState[category] = [...currentSelection, name];
            }
          });

          return nextState;
        });

        setSelectedKpiOrder((previousOrder) => {
          const missingSavedKeys = allSavedKpiKeysInCatalog.filter(
            (kpiKey) => !previousOrder.includes(kpiKey),
          );
          if (missingSavedKeys.length === 0) {
            return previousOrder;
          }
          return [...previousOrder, ...missingSavedKeys];
        });

        return;
      }

      setSelectedKpisByCategory((previous) => {
        const nextState = {};

        Object.entries(previous).forEach(([category, names]) => {
          const keptNames = names.filter(
            (name) => !allSavedKpiKeysInCatalog.includes(makeKpiKey(category, name)),
          );
          if (keptNames.length > 0) {
            nextState[category] = keptNames;
          }
        });

        return nextState;
      });

      setSelectedKpiOrder((previousOrder) =>
        previousOrder.filter((kpiKey) => !allSavedKpiKeysInCatalog.includes(kpiKey)),
      );
    });
  };

  const openEditSavedKpisModal = async () => {
    setEditSavedKpisMessage("");
    setShowEditSavedKpisError(false);
    setShowEditSavedKpisModal(true);
    await loadSavedKpis();
  };

  const handleDeleteSavedKpi = async (kpiId) => {
    const normalizedKpiId = Number(kpiId);
    if (!Number.isInteger(normalizedKpiId) || normalizedKpiId <= 0) {
      return;
    }

    setDeletingSavedKpiIds((previous) => ({
      ...previous,
      [normalizedKpiId]: true,
    }));
    setEditSavedKpisMessage("");
    setShowEditSavedKpisError(false);

    try {
      await requestWithFallback(
        `/api/kpis/saved/${encodeURIComponent(normalizedKpiId)}`,
        {
          method: "DELETE",
        },
      );

      await loadSavedKpis();
      setEditSavedKpisMessage("Saved KPI deleted successfully.");
      setShowEditSavedKpisError(false);
    } catch (error) {
      console.error("Error deleting saved KPI:", error);
      setEditSavedKpisMessage(
        error?.message || "Unable to delete saved KPI. Please try again.",
      );
      setShowEditSavedKpisError(true);
    } finally {
      setDeletingSavedKpiIds((previous) => {
        const next = { ...previous };
        delete next[normalizedKpiId];
        return next;
      });
    }
  };

  const handleKpiToggle = (category, kpiName) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    const kpiKey = makeKpiKey(category, kpiName);
    const isCurrentlySelected = (selectedKpisByCategory[category] || []).includes(kpiName);

    setSelectedKpiCategory(category);
    startTransition(() => {
      setSelectedKpisByCategory((previous) => {
        const currentSelection = previous[category] || [];
        const nextSelection = isCurrentlySelected
          ? currentSelection.filter((item) => item !== kpiName)
          : [...currentSelection, kpiName];

        if (nextSelection.length === 0) {
          const nextState = { ...previous };
          delete nextState[category];
          return nextState;
        }

        return {
          ...previous,
          [category]: nextSelection,
        };
      });

      setSelectedKpiOrder((previousOrder) =>
        isCurrentlySelected
          ? previousOrder.filter((item) => item !== kpiKey)
          : [...previousOrder, kpiKey],
      );
    });
  };

  const handleDragStart = (kpiKey) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    setDraggedKpiKey(kpiKey);
    setKpiDropIndicator(null);
  };

  const handleDragOver = (event, targetKpiKey) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    if (!draggedKpiKey || draggedKpiKey === targetKpiKey) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2;
    setKpiDropIndicator((prev) =>
      prev && prev.key === targetKpiKey && prev.after === after ? prev : { key: targetKpiKey, after }
    );
  };

  const handleDrop = (event, targetKpiKey) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (!draggedKpiKey || draggedKpiKey === targetKpiKey) {
      setKpiDropIndicator(null);
      setDraggedKpiKey("");
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const insertAfter = event.clientY > rect.top + rect.height / 2;
    startTransition(() => {
      setSelectedKpiOrder((previousOrder) => {
        const nextOrder = previousOrder.filter((item) => item !== draggedKpiKey);
        const targetIndex = nextOrder.indexOf(targetKpiKey);
        if (targetIndex === -1) return previousOrder;
        nextOrder.splice(insertAfter ? targetIndex + 1 : targetIndex, 0, draggedKpiKey);
        return nextOrder;
      });
    });
    setKpiDropIndicator(null);
    setDraggedKpiKey("");
  };

  const handleDragEnd = () => {
    setDraggedKpiKey("");
    setKpiDropIndicator(null);
  };

  const handleCategoryDragStart = (event, category) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    event.stopPropagation();
    setDraggedCategoryKey(category);
    setCatDropIndicator(null);
  };

  const handleCategoryDragOver = (event, targetCategory) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    if (!draggedCategoryKey || draggedCategoryKey === targetCategory) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2;
    setCatDropIndicator((prev) =>
      prev && prev.category === targetCategory && prev.after === after ? prev : { category: targetCategory, after }
    );
  };

  const handleCategoryDrop = (event, targetCategory) => {
    if (!isKpiInteractionEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (!draggedCategoryKey || draggedCategoryKey === targetCategory) {
      setCatDropIndicator(null);
      setDraggedCategoryKey("");
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const insertAfter = event.clientY > rect.top + rect.height / 2;
    startTransition(() => {
      setSelectedKpiOrder((previousOrder) => {
        const draggedKeys = previousOrder.filter((k) => {
          const parsed = parseKpiKey(k);
          return parsed && parsed.category === draggedCategoryKey;
        });
        const remainingKeys = previousOrder.filter((k) => {
          const parsed = parseKpiKey(k);
          return !parsed || parsed.category !== draggedCategoryKey;
        });
        // find first or last index of target category in remaining keys
        const targetIndices = remainingKeys.reduce((acc, k, i) => {
          const parsed = parseKpiKey(k);
          if (parsed && parsed.category === targetCategory) acc.push(i);
          return acc;
        }, []);
        if (targetIndices.length === 0) return previousOrder;
        const insertAt = insertAfter
          ? targetIndices[targetIndices.length - 1] + 1
          : targetIndices[0];
        remainingKeys.splice(insertAt, 0, ...draggedKeys);
        return remainingKeys;
      });
    });
    setCatDropIndicator(null);
    setDraggedCategoryKey("");
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategoryKey("");
    setCatDropIndicator(null);
  };

  const handleGenerateBenchmarks = () => {
    const selectedMatches = tableRows.flatMap((selectedRow) =>
      data.filter(
        (kpi) =>
          kpi.KPI_Category === selectedRow.category &&
          kpi.KPI_Name === selectedRow.kpi,
      ),
    );

    const selectedKpiIds = [...new Set(
      selectedMatches
        .map((kpi) => Number(kpi.id ?? kpi.kpi_id))
        .filter((id) => Number.isInteger(id) && id > 0),
    )];

    try {
      localStorage.setItem("selectedKpiRows", JSON.stringify(tableRows));
      localStorage.setItem("selectedKpiIds", JSON.stringify(selectedKpiIds));
    } catch {
      // ignore storage errors
    }
    console.log("Selected KPI IDs for benchmarking:", selectedKpiIds);
    console.log("Selected KPI rows for benchmarking:", tableRows);
    if (typeof onContinue === "function") {
      onContinue();
      return;
    }
    navigate(
      compareCompanyView
        ? "/company-benchmarking-summary"
        : "/benchmarking-summary",
    );
  };

  const handleKpiSearch = async () => {
    const trimmedQuery = kpiSearchQuery.trim();
    setLoading(true);
    setKpiSearchMessage("");

    try {
      if (!trimmedQuery) {
        const response = await axios.get(KPI_API_URL, { withCredentials: true });
        startTransition(() => {
          setData(extractKpiRows(response.data));
        });
        return;
      }

      const response = await axios.get(KPI_SEARCH_API_URL, {
        withCredentials: true,
        params: {
          query: trimmedQuery,
          limit: 15,
          skip: 0,
          include_sql_equivalent: false,
        },
      });

      const nextData = extractKpiRows(response.data);
      startTransition(() => {
        setData(nextData);
        if (nextData.length === 0) {
          setKpiSearchMessage(`No KPIs found for "${trimmedQuery}".`);
        }
      });
    } catch (error) {
      console.error("Error searching KPIs:", error);
      startTransition(() => {
        setData([]);
        setKpiSearchMessage("Unable to search KPIs right now. Please try again.");
      });
    } finally {
      setLoading(false);
    }
  };

  function openAddKpiModal() {
    setNewKpiName('');
    setNewKpiSource('');
    setNewKpiCategory('');
    setAddKpiApiMessage('');
    setShowAddKpiModal(true);
    setShowErrorOnAddKpi(false);
    setAddKpiLoading(false);
    setNewKpiFunction('');
    setFieldErrors({ name: '', description: '', category: '', function: '' });
  }

  async function submitNewKpi() {
    const trimmedName = newKpiName.trim();
    const trimmedCategory = newKpiCategory.trim();
    const trimmedFunction = newKpiFunction.trim();
    const trimmedDescription = newKpiDescription.trim();
    setAddKpiApiMessage('');
    
    // Validate fields
    const errors = {
      name: !trimmedName ? 'KPI Name is required' : '',
      description: !trimmedDescription ? 'Description is required' : '',
      category: !trimmedCategory ? 'Category is required' : '',
      function: !trimmedFunction ? 'Function is required' : '',
    };

    if (errors.name || errors.description || errors.category || errors.function) {
      setFieldErrors(errors);
      setShowErrorOnAddKpi(true);
      setAddKpiLoading(false);
      return;
    }

    setFieldErrors({ name: '', description: '', category: '', function: '' });
    setAddKpiLoading(true);

    try {
      const payload = {
        kpi_name: trimmedName,
        description: trimmedDescription,
        kpi_category: trimmedCategory,
        function: trimmedFunction,
      };
      
      console.log('Submitting KPI with payload:', payload);
      
      const resp = await requestWithFallback('/api/kpi/add-generated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }, 120000);
      const data = await resp.json();

      if (data?.status === 'exists') {
        setAddKpiApiMessage('KPI already exists');
        setShowErrorOnAddKpi(true);
        setAddKpiLoading(false);
        return;
      }
      if (data?.status === 'success') {
        setAddKpiApiMessage('New KPI added successfully');
        setShowErrorOnAddKpi(false);
        setAddKpiLoading(false);

        // Reset form fields while keeping modal open so user can see success state.
        setNewKpiName('');
        setNewKpiDescription('');
        setNewKpiCategory('');
        setNewKpiFunction('');
        
        // Reload KPIs from API
        const kpiResp = await axios.get(KPI_API_URL, { withCredentials: true });
        const nextData = extractKpiRows(kpiResp.data);
        setData(nextData);

        setTimeout(() => {
          setShowAddKpiModal(false);
          setAddKpiApiMessage('');
        }, 1500);
      }
      
      const insertedId = Number(data?.inserted_id);
      if (Number.isFinite(insertedId)) {
        try {
          const raw = localStorage.getItem(NEWLY_ADDED_KPI_IDS_KEY);
          const parsed = raw ? JSON.parse(raw) : [];
          const currentIds = Array.isArray(parsed)
            ? parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id))
            : [];
          const nextIds = Array.from(new Set([...currentIds, insertedId]));
          localStorage.setItem(NEWLY_ADDED_KPI_IDS_KEY, JSON.stringify(nextIds));
        } catch {
          // Ignore storage errors.
        }
        await runCalculations([insertedId]);
      }
    } catch (err) {
      console.error('Error adding KPI:', err);
      setAddKpiLoading(false);
      setShowErrorOnAddKpi(true);
      setAddKpiApiMessage(err.message || 'Failed to add KPI. Please try again.');
    }
  }

  async function runCalculations(explicitKpiIds = null) {
    try {
      await runSharedKpiCalculations({
        companyIds: null,
        kpiIds: explicitKpiIds,
      });
    } catch (e) {
      alert(`Run calculations failed: ${e.message}`);
    }
  }

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-indigo-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-0 pb-0 overflow-visible">
        {/* <div className="max-w-7xl mx-auto px-6 pt-4">
          <WorkflowStatusBar activeSteps={ tableRows.length > 0 ? 2 : 1} />
        </div> */}

        {/* <div className="text-center pt-3 pb-4">
          <span className="inline-flex items-center rounded-full border border-sky-300/80 bg-sky-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-sky-800 dark:border-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
            Benchmark Setup: Step 2 of 3
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
            Please define the benchmark population
          </h2>
          <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-300">
            Select KPI categories and KPIs to build your benchmarking comparison set.
          </p>
        </div> */}

        <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-[0_10px_26px_rgba(16,24,40,0.08)] p-5 md:p-6 mb-8">
          {/* <div className="w-full h-1.5 rounded-full bg-sky-100 dark:bg-sky-900/40 mb-5 overflow-hidden">
            <div
              className="h-full rounded-full bg-sky-500 transition-all duration-500 ease-out"
              style={{ width: `${stripWidthPercent}%` }}
            ></div>
          </div> */}

          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-700 border-t-sky-700"
                aria-label="Loading"
              ></div>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
                Loading data...
              </p>
            </div>
          )}

          {!loading && (
            <div className="relative">
              {!isKpiInteractionEnabled && (
                <div className="absolute inset-0 z-20 rounded-xl bg-white/60 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center p-4 text-center">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Select industry parameter to enable KPI selection.
                  </p>
                </div>
              )}

              <div className={isKpiInteractionEnabled ? "" : "pointer-events-none opacity-60"}>
              <div className="mb-3 flex items-center  justify-between  gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                <div className="flex items-center gap-2">
                <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                <span>KPI Selection ({tableRows.length} selected)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    title="Edit Saved KPI's"
                    className="inline-flex items-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 disabled:opacity-60"
                    onClick={openEditSavedKpisModal}
                    disabled={savedKpiLoading}
                  >
                    Edit Saved KPI's
                  </button>
                  <button
                    title='Add New KPI'
                    className="inline-flex items-center rounded-xl bg-lime-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lime-700 disabled:opacity-60"
                    onClick={openAddKpiModal}
                    disabled={false}
                  >
                    + Add Custom
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                      <span>KPI Catalog</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={areAllSavedKpisSelected}
                          onChange={handleSavedKpisCheckboxChange}
                          disabled={savedKpiLoading || allSavedKpiKeysInCatalog.length === 0 || !isKpiInteractionEnabled}
                          className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500 disabled:cursor-not-allowed"
                        />
                        <span>Saved KPI's</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleSaveSelectedKpis}
                        disabled={!canSaveSelectedKpis}
                        className="inline-flex items-center rounded-lg border border-lime-600 bg-lime-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-lime-700 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 dark:disabled:border-gray-700 dark:disabled:bg-gray-700"
                      >
                        {saveSelectedKpisLoading ? "Saving..." : "Save Selected KPI's"}
                      </button>
                    </div>
                  </div>

                  {saveSelectedKpisMessage && (
                    <p
                      className={`mb-3 text-xs font-medium ${
                        showSaveSelectedKpisError
                          ? "text-red-600 dark:text-red-400"
                          : "text-emerald-700 dark:text-emerald-400"
                      }`}
                    >
                      {saveSelectedKpisMessage}
                    </p>
                  )}

                  <div className="mb-3 flex items-center gap-2">
                    <input
                      type="text"
                      value={kpiSearchQuery}
                      onChange={(event) => setKpiSearchQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleKpiSearch();
                        }
                      }}
                      placeholder="Search KPIs"
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-lime-500 focus:ring-2 focus:ring-lime-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-lime-900/40"
                    />
                    <button
                      type="button"
                      onClick={handleKpiSearch}
                      className="inline-flex items-center justify-center rounded-lg border border-lime-500 bg-lime-500 px-3 py-2 text-white transition hover:bg-lime-600"
                      aria-label="Search KPIs"
                      title="Search"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M9 3a6 6 0 104.472 10.002l2.763 2.763a1 1 0 001.414-1.414l-2.763-2.763A6 6 0 009 3zm-4 6a4 4 0 118 0 4 4 0 01-8 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {kpiSearchMessage && (
                    <p className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {kpiSearchMessage}
                    </p>
                  )}

                  <div className="max-h-[440px] overflow-y-auto pr-1">
                    {kpiCategories.map((category) => {
                      const kpisInCategory = data.filter(
                        (kpi) => kpi.KPI_Category === category,
                      );
                      const selectedInCategory = selectedKpisByCategory[category] || [];

                      return (
                        <div key={category} className="mb-5 last:mb-0">
                          <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-lime-700 dark:text-lime-400">
                            {category} ({kpisInCategory.length})
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {kpisInCategory.map((kpi) => {
                              const isSelected = selectedInCategory.includes(kpi.KPI_Name);

                              return (
                                <button
                                  type="button"
                                  key={kpi.id || `${kpi.KPI_Category}-${kpi.KPI_Name}`}
                                  onClick={() => handleKpiToggle(category, kpi.KPI_Name)}
                                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                                    isSelected
                                      ? "border-lime-500 bg-lime-500 text-white"
                                      : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                                  }`}
                                >
                                  {kpi.KPI_Name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                      <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                      <span>Selected KPIs ({tableRows.length})</span>
                    </div>
                    <p className="text-xs text-gray-400">Drag to reorder</p>
                  </div>

                  <div className="max-h-[440px] overflow-y-auto rounded-lg border border-gray-100 p-2 dark:border-gray-800">
                    {tableRows.length === 0 && (
                      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                        No KPI selected.
                      </p>
                    )}

                    {[...new Set(tableRows.map((row) => row.category))]
                      .map((category) => ({
                        category,
                        kpis: selectedKpisGroupedByCategory[category] || [],
                      }))
                      .map(({ category, kpis }) => (
                      <div
                        key={category}
                        className={`mb-4 rounded-lg last:mb-0 ${
                          draggedCategoryKey === category ? "opacity-50" : ""
                        }`}
                        onDragOver={(event) => handleCategoryDragOver(event, category)}
                        onDrop={(event) => handleCategoryDrop(event, category)}
                      >
                        {catDropIndicator && catDropIndicator.category === category && !catDropIndicator.after && (
                          <div className="h-0.5 rounded-full bg-lime-500 mb-1" />
                        )}
                        <h4
                          draggable
                          onDragStart={(event) => handleCategoryDragStart(event, category)}
                          onDragEnd={handleCategoryDragEnd}
                          className="mb-2 flex cursor-grab items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-lime-700 select-none dark:text-lime-400 active:cursor-grabbing"
                        >
                          <svg className="h-3 w-3 shrink-0 text-lime-400" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
                            <circle cx="2.5" cy="2" r="1.5"/><circle cx="7.5" cy="2" r="1.5"/>
                            <circle cx="2.5" cy="8" r="1.5"/><circle cx="7.5" cy="8" r="1.5"/>
                            <circle cx="2.5" cy="14" r="1.5"/><circle cx="7.5" cy="14" r="1.5"/>
                          </svg>
                          {category} ({kpis.length})
                        </h4>
                        <div className="space-y-1">
                          {kpis.map((kpiName) => {
                            const rowKpiKey = makeKpiKey(category, kpiName);
                            const isDropTargetBefore = kpiDropIndicator && kpiDropIndicator.key === rowKpiKey && !kpiDropIndicator.after;
                            const isDropTargetAfter = kpiDropIndicator && kpiDropIndicator.key === rowKpiKey && kpiDropIndicator.after;

                            return (
                              <React.Fragment key={`${category}-${kpiName}`}>
                                {isDropTargetBefore && (
                                  <div className="h-0.5 rounded-full bg-lime-500 my-0.5" />
                                )}
                              <div
                                draggable
                                onDragStart={() => handleDragStart(rowKpiKey)}
                                onDragOver={(event) => handleDragOver(event, rowKpiKey)}
                                onDrop={(event) => handleDrop(event, rowKpiKey)}
                                onDragEnd={handleDragEnd}
                                className={`flex cursor-move items-center justify-between gap-3 rounded-md px-2 py-2 text-sm text-gray-700 dark:text-gray-200 ${
                                  draggedKpiKey === rowKpiKey
                                    ? "bg-lime-100/70 dark:bg-lime-900/40"
                                    : "bg-gray-50 dark:bg-gray-800"
                                }`}
                              >
                                <span className="truncate">
                                  <span aria-hidden="true" className="mr-1 text-gray-400">▲</span>
                                  <span aria-hidden="true" className="mr-2 text-gray-400">▼</span>
                                  {kpiName}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleKpiToggle(category, kpiName)}
                                  className="text-xs font-medium text-gray-400 hover:text-red-500"
                                  aria-label={`Remove ${kpiName}`}
                                >
                                  x
                                </button>
                              </div>
                                {isDropTargetAfter && (
                                  <div className="h-0.5 rounded-full bg-lime-500 my-0.5" />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                        {catDropIndicator && catDropIndicator.category === category && catDropIndicator.after && (
                          <div className="h-0.5 rounded-full bg-lime-500 mt-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}
        </div>

        {/* {(compareCompanyView || tableRows.length > 0) && (
          <div className="w-full max-w-6xl mx-auto pt-6">
            <div
              className={`flex flex-col gap-4 ${
                compareCompanyView ? "md:flex-row md:items-start" : ""
              }`}
            >
              {compareCompanyView && (
                <div
                  tabIndex={0}
                  className="bg-sky-50 dark:bg-sky-950/30 p-5 rounded-2xl border border-sky-200 dark:border-sky-800 transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 text-center w-full md:max-w-sm md:flex-shrink-0 shadow-sm"
                >
                  <h2 className="text-xl font-bold text-sky-900 dark:text-sky-200 mt-0 mb-2">
                    Compare to Company
                  </h2>
                  <p className="text-sm text-sky-800/80 dark:text-sky-100/80 leading-relaxed whitespace-pre-line">
                    File name: {uploadedTempFileName}
                    {"\n"}
                    Status: Ready for comparison
                  </p>
                </div>
              )}

              {tableRows.length > 0 && (
                <div
                  className={`w-full overflow-x-auto bg-white dark:bg-gray-950 rounded-2xl border border-emerald-200 dark:border-gray-700 shadow-[0_10px_24px_rgba(16,185,129,0.12)] ${
                    compareCompanyView
                      ? "md:ml-auto md:max-w-4xl"
                      : "max-w-4xl mx-auto"
                  }`}
                >
                  <table className="min-w-full text-sm text-left text-gray-700 dark:text-gray-200">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold border-r border-emerald-200 dark:border-gray-700">
                          Selected KPI Category
                        </th>
                        <th className="px-4 py-3 font-semibold">
                          Selected KPI
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row) => (
                        <tr
                          key={`${row.category}-${row.kpi}`}
                          className="border-b border-gray-200 dark:border-gray-800 last:border-b-0 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20"
                        >
                          <td className="px-4 py-3 border-r border-gray-200 dark:border-gray-800">{row.category}</td>
                          <td className="px-4 py-3">{row.kpi}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )} */}

        {tableRows.length > 0 && !hideContinueButton && (
          <div className="flex justify-center pt-6">
            <button
              onClick={handleGenerateBenchmarks}
              className="inline-flex items-center gap-2 px-7 py-2.5 rounded-xl font-semibold tracking-wide transition-all bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 dark:hover:shadow-none"
            >
              {compareCompanyView
                ? "Next: Compare Company vs Benchmarks"
                : "Next: Generate Benchmarks"}
              <span aria-hidden="true">-&gt;</span>
            </button>
          </div>
        )}

        {/* Edit Saved KPI Modal */}
        {showEditSavedKpisModal &&
          ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[105] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setShowEditSavedKpisModal(false);
                }
              }}
            >
              <div className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Saved KPI's</h2>
                  <button
                    onClick={() => setShowEditSavedKpisModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {editSavedKpisMessage && (
                  <div
                    className={`mb-4 rounded-lg p-3 text-sm ${
                      showEditSavedKpisError
                        ? "bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200"
                        : "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200"
                    }`}
                  >
                    {editSavedKpisMessage}
                  </div>
                )}

                {savedKpiLoading ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                    Loading saved KPIs...
                  </div>
                ) : savedKpiItems.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                    No saved KPIs found.
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                    {savedKpiItems.map((item) => {
                      const itemKpiId = Number(item?.kpi_id);
                      const isDeleting = Boolean(deletingSavedKpiIds[itemKpiId]);
                      return (
                        <div
                          key={`${item?.saved_id || itemKpiId}-${itemKpiId}`}
                          className="flex items-center justify-between gap-4 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {item?.kpi_name || `KPI ID ${itemKpiId}`}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              KPI ID: {itemKpiId}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteSavedKpi(itemKpiId)}
                            disabled={isDeleting || !Number.isInteger(itemKpiId) || itemKpiId <= 0}
                            className="inline-flex items-center justify-center rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-900/20"
                            title="Delete saved KPI"
                            aria-label={`Delete saved KPI ${item?.kpi_name || itemKpiId}`}
                          >
                            {isDeleting ? (
                              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                                <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="3" className="opacity-90" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-8 0l1 12a1 1 0 001 1h6a1 1 0 001-1l1-12" />
                              </svg>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}

        {/* Add KPI Modal */}
        {showAddKpiModal &&
          ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setShowAddKpiModal(false);
                }
              }}
            >
              <div className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  Note: KPI formula and SQL queries are auto-generated from the KPI name, description, and selected category. Enter these details carefully to ensure accurate calculations.
                </div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New KPI</h2>
                  <button
                    onClick={() => setShowAddKpiModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {addKpiApiMessage && (
                  <div className={`mb-4 p-3 rounded-lg ${showErrorOnAddKpi ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200' : 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'}`}>
                    {addKpiApiMessage}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">KPI Name *</label>
                    <input
                      type="text"
                      value={newKpiName}
                      onChange={(e) => {
                        setNewKpiName(e.target.value);
                        if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 ${
                        fieldErrors.name
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Enter KPI name"
                    />
                    {fieldErrors.name && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>Description *</span>
                      <span className="group relative inline-flex items-center">
                       <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-black-400 transition-colors group-hover:text-emerald-500 dark:text-gray-500 dark:group-hover:text-emerald-400"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                        <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-80 -translate-x-1/2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-normal text-gray-700 shadow-lg group-hover:block dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                          Write the calculation in words using exact metric names and simple operators. Example: `Gross Margin = Revenue - Cost of Goods Sold`
                        </span>
                      </span>
                    </label>
                    <textarea
                      value={newKpiDescription}
                      onChange={(e) => {
                        setNewKpiDescription(e.target.value);
                        if (fieldErrors.description) setFieldErrors({ ...fieldErrors, description: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 h-20 resize-none ${
                        fieldErrors.description
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Enter KPI description"
                    />
                    {fieldErrors.description && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldErrors.description}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>Category *</span>
                      <span className="group relative inline-flex items-center">
                       <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-black-400 transition-colors group-hover:text-emerald-500 dark:text-gray-500 dark:group-hover:text-emerald-400"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                        <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-80 -translate-x-1/2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-normal text-gray-700 shadow-lg group-hover:block dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                          Choose the correct category to ensure this KPI is mapped and calculated correctly.
                        </span>
                      </span>
                    </label>
                    <select
                      value={newKpiCategory}
                      onChange={(e) => {
                        setNewKpiCategory(e.target.value);
                        if (fieldErrors.category) setFieldErrors({ ...fieldErrors, category: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 ${
                        fieldErrors.category
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <option value="">Select Category</option>
                      {kpiCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    {fieldErrors.category && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldErrors.category}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Function *</label>
                    <select
                      value={newKpiFunction}
                      onChange={(e) => {
                        setNewKpiFunction(e.target.value);
                        if (fieldErrors.function) setFieldErrors({ ...fieldErrors, function: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 ${
                        fieldErrors.function
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <option value="">Select Function</option>
                      {kpiFunctions.map((func) => (
                        <option key={func} value={func}>{func}</option>
                      ))}
                    </select>
                    {fieldErrors.function && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldErrors.function}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowAddKpiModal(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitNewKpi}
                    disabled={addKpiLoading}
                    className="flex-1 px-4 py-2 rounded-lg bg-lime-600 text-white hover:bg-lime-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {addKpiLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      'Add KPI'
                    )}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default KpiSelectors;
